'use client';

import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
    CountryCode,
    formatIncompletePhoneNumber,
    getExampleNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { useState } from 'react';
import { useClientTranslation } from '../data/utils/useClientTranslation';
import { DataField } from '../data/dataType';

interface PhoneNumberInputProps {
    dataField: DataField;
    tag: string;
    locale: string;
    displayError: boolean;
    onChange: (value: string | null) => void;
    value: string | undefined;
    error?: string | null;
    touched?: boolean;
    setTouched?: (
        field: string,
        touched: boolean,
        shouldValidate?: boolean,
    ) => void;
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
    dataField,
    displayError,
    locale,
    onChange,
    value,
    error,
    touched,
    setTouched,
}) => {
    const [country, setCountry] = useState<CountryCode>('CA');
    const [localError, setLocalError] = useState<string | null>(null);
    const { translation } = useClientTranslation(locale, 'fieldsInput');

    const getDynamicExample = () => {
        try {
            const exampleNumber = getExampleNumber(
                country as CountryCode,
                examples,
            );
            return formatIncompletePhoneNumber(
                exampleNumber.number,
                country as CountryCode,
            );
        } catch {
            return (
                translation?.phoneExampleUnavailable || 'Exemple non disponible'
            );
        }
    };

    const validatePhoneNumber = (number: string | undefined) => {
        if (dataField.required && !number) {
            setLocalError(
                dataField.requiredMessage[locale] || 'Ce champ est requis 2.',
            );
            return false;
        }
        if (number && !isValidPhoneNumber(number)) {
            setLocalError(
                dataField.invalidMessage[locale] || 'Numéro invalide.',
            );
            return false;
        }
        setLocalError(null);
        return true;
    };

    const handleBlur = () => {
        if (setTouched) {
            setTouched(dataField.name, true, true);
        }
    };

    const handleChange = (number: string | undefined) => {
        onChange(number || null);
        validatePhoneNumber(number);
    };

    return (
        <div className="w-full flex flex-col gap-2">
            <PhoneInput
                className={`border rounded-md w-full px-4 py-2 focus:ring-2 ${
                    (localError || error) && touched
                        ? 'border-red-500 focus:ring-red-500'
                        : 'focus:ring-primary'
                }`}
                defaultCountry={country}
                id={dataField.name}
                onBlur={handleBlur}
                onChange={e => handleChange(e)}
                onCountryChange={newCountry => setCountry(newCountry || 'CA')}
                placeholder={translation?.phone || ''}
                value={value}
            />
            {(localError || error) && displayError && touched && (
                <p className="text-sm text-red-500">{localError || error}</p>
            )}
            <p className="text-sm text-gray-500">
                {translation?.phoneExample || ''} : {getDynamicExample()}
            </p>
        </div>
    );
};
